import React from 'react';
import Select from 'react-select';
import ToggleOff from '@mui/icons-material/ToggleOffOutlined';
import ToggleOn from '@mui/icons-material/ToggleOn';
import ColorPicker from './ColorPicker';
import LabelColorSlider from './LabelColorSlider';
import {
  ARROW_ANCHOR_OPTIONS,
  ARROW_SHAPE_OPTIONS,
  findDatastreamMeta,
} from '../widgets/assetStatusUtils';

const toggleStyle = { color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' };

const DASH_OPTIONS = [
  { label: 'Solid', value: 'none' },
  { label: 'Dashed', value: 'dashed' },
  { label: 'Dotted', value: 'dotted' },
];

const normalizePointField = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '';
  return Math.max(0, Math.min(100, parsed));
};

const buildDatastreamOptions = (datastreams = []) => (datastreams || [])
  .filter(Boolean)
  .map((ds) => ({
    label: `${ds.name || ds.pin || 'Datastream'}${ds.pin ? ` [${ds.pin}]` : ''}`,
    value: ds.id,
    pin: ds.pin,
    datastream: ds,
  }));

const getAvailableDatastreams = ({ edit, devices, validDatastreams }) => {
  if (Array.isArray(devices) && devices.length > 0) {
    const selectedDevice = devices.find((device) => Number(device?.id) === Number(edit.deviceId));
    return (selectedDevice?.template?.datastreams || []).filter(Boolean);
  }
  return (validDatastreams || []).filter(Boolean);
};

const selectValueFor = (options, dataId, pin) => (
  options.find((option) => String(option.value) === String(dataId)) ||
  options.find((option) => String(option.pin || '') === String(pin || '')) ||
  null
);

const getMinMaxForDatastream = (datastream) => {
  const min = Number(datastream?.min);
  const max = Number(datastream?.max);
  return {
    min: Number.isFinite(min) ? min : 0,
    max: Number.isFinite(max) ? max : 100,
  };
};

const StatusArrowSettings = ({ edit, setEdit, devices, validDatastreams, deviceOptions = [] }) => {
  const availableDatastreams = getAvailableDatastreams({ edit, devices, validDatastreams });
  const datastreamOptions = buildDatastreamOptions(availableDatastreams);
  const selectedDatastream = findDatastreamMeta(availableDatastreams, edit.dataId, edit.dataPin);
  const selectedOption = selectValueFor(datastreamOptions, edit.dataId, edit.dataPin);
  const bounds = getMinMaxForDatastream(selectedDatastream);
  const updateProps = (patch) => setEdit((prev) => ({
    ...prev,
    props: { ...(prev.props || {}), ...patch },
  }));

  return (
    <>
      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Arrow shape</label>
        <Select
          classNamePrefix="react-select"
          options={ARROW_SHAPE_OPTIONS}
          value={ARROW_SHAPE_OPTIONS.find((option) => option.value === (edit.props?.shape || 'elbow')) || ARROW_SHAPE_OPTIONS[1]}
          onChange={(option) => updateProps({ shape: option?.value || 'elbow' })}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Start anchor</label>
          <Select
            classNamePrefix="react-select"
            options={ARROW_ANCHOR_OPTIONS}
            value={ARROW_ANCHOR_OPTIONS.find((option) => option.value === (edit.props?.startAnchor || 'left')) || ARROW_ANCHOR_OPTIONS[0]}
            onChange={(option) => updateProps({ startAnchor: option?.value || 'left' })}
          />
        </div>
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>End anchor</label>
          <Select
            classNamePrefix="react-select"
            options={ARROW_ANCHOR_OPTIONS}
            value={ARROW_ANCHOR_OPTIONS.find((option) => option.value === (edit.props?.endAnchor || 'right')) || ARROW_ANCHOR_OPTIONS[1]}
            onChange={(option) => updateProps({ endAnchor: option?.value || 'right' })}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Stroke</label>
          <input
            className="add-column"
            type="number"
            min="1"
            max="18"
            value={edit.props?.strokeWidth ?? 3}
            onChange={(event) => updateProps({ strokeWidth: event.target.value })}
          />
        </div>
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Bend</label>
          <input
            className="add-column"
            type="number"
            min="0"
            max="100"
            value={edit.props?.bend ?? 50}
            onChange={(event) => updateProps({ bend: event.target.value })}
          />
        </div>
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Dash</label>
          <Select
            classNamePrefix="react-select"
            options={DASH_OPTIONS}
            value={DASH_OPTIONS.find((option) => option.value === (edit.props?.dash || 'none')) || DASH_OPTIONS[0]}
            onChange={(option) => updateProps({ dash: option?.value || 'none' })}
          />
        </div>
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Arrow color datastream</label>
        {Array.isArray(devices) && devices.length > 0 ? (
          <Select
            classNamePrefix="react-select"
            options={deviceOptions}
            value={deviceOptions.find((device) => Number(device.value) === Number(edit.deviceId)) || null}
            onChange={(option) => setEdit((prev) => ({
              ...prev,
              deviceId: option ? Number(option.value) : 0,
              dataId: 0,
              dataPin: '',
            }))}
            isClearable
          />
        ) : null}
        <Select
          classNamePrefix="react-select"
          options={datastreamOptions}
          value={selectedOption}
          onChange={(option) => setEdit((prev) => ({
            ...prev,
            dataId: option?.datastream?.id ?? 0,
            dataPin: option?.datastream?.pin ?? '',
          }))}
          placeholder="Choose datastream"
          isClearable
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Change arrow color by value</label>
        {edit.props?.changeColorBasedOnValue ? (
          <ToggleOn style={toggleStyle} onClick={() => updateProps({ changeColorBasedOnValue: false })} />
        ) : (
          <ToggleOff style={toggleStyle} onClick={() => updateProps({ changeColorBasedOnValue: true })} />
        )}
      </div>

      {edit.props?.changeColorBasedOnValue ? (
        <>
          <div className="form-item">
            <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Smooth gradient transition</label>
            {edit.props?.colorGrading ? (
              <ToggleOn style={toggleStyle} onClick={() => updateProps({ colorGrading: false })} />
            ) : (
              <ToggleOff style={toggleStyle} onClick={() => updateProps({ colorGrading: true })} />
            )}
          </div>
          <LabelColorSlider
            minValue={bounds.min}
            maxValue={bounds.max}
            stops={edit.props?.colors || []}
            colorGrading={Boolean(edit.props?.colorGrading)}
            setStops={(updated) => updateProps({ colors: updated })}
          />
        </>
      ) : (
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Fixed arrow color</label>
          <ColorPicker id="status-arrow-color" color={edit.props?.color || '#26b4ff'} setColor={(color) => updateProps({ color })} />
        </div>
      )}

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Focus value preview</label>
        <input
          className="add-column"
          value={edit.props?.value ?? ''}
          onFocus={() => {
            if (selectedDatastream?.defaultValue !== undefined && (edit.props?.value === undefined || edit.props?.value === '')) {
              updateProps({ value: selectedDatastream.defaultValue });
            }
          }}
          onChange={(event) => updateProps({ value: event.target.value })}
          placeholder="Preview value"
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Paint-style endpoints (%)</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr)) auto', gap: 10, alignItems: 'end' }}>
          {[
            ['startPoint', 'x', 'Start X'],
            ['startPoint', 'y', 'Start Y'],
            ['endPoint', 'x', 'End X'],
            ['endPoint', 'y', 'End Y'],
          ].map(([pointKey, axis, label]) => (
            <div className="form-item" key={`${pointKey}-${axis}`} style={{ marginBottom: 0 }}>
              <label className="form-lbl" style={{ color: 'var(--header-color)' }}>{label}</label>
              <input
                className="add-column"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={edit.props?.[pointKey]?.[axis] ?? ''}
                placeholder="Auto"
                onChange={(event) => {
                  const normalized = normalizePointField(event.target.value);
                  updateProps({
                    [pointKey]: {
                      ...(edit.props?.[pointKey] || {}),
                      [axis]: normalized === '' ? undefined : normalized,
                    },
                  });
                }}
              />
            </div>
          ))}
          <button
            type="button"
            className="btn cancel-btn"
            onClick={() => updateProps({ startPoint: null, endPoint: null })}
          >
            Reset
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {[
          ['showStartDot', 'Start dot'],
          ['showEndArrow', 'Arrow head'],
          ['reverse', 'Reverse direction'],
          ['animated', 'Animated flow'],
        ].map(([key, label]) => (
          <div className="form-item" key={key}>
            <label className="form-lbl" style={{ color: 'var(--header-color)' }}>{label}</label>
            {edit.props?.[key] === false ? (
              <ToggleOff style={toggleStyle} onClick={() => updateProps({ [key]: true })} />
            ) : key === 'reverse' || key === 'animated' ? (
              edit.props?.[key] ? (
                <ToggleOn style={toggleStyle} onClick={() => updateProps({ [key]: false })} />
              ) : (
                <ToggleOff style={toggleStyle} onClick={() => updateProps({ [key]: true })} />
              )
            ) : (
              <ToggleOn style={toggleStyle} onClick={() => updateProps({ [key]: false })} />
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default StatusArrowSettings;
